import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Tag, BookOpen } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function formatDate(str) {
  if (!str) return '';
  try {
    return new Date(str).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  } catch {
    return str;
  }
}

export default function BlogPostPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    fetch(`${API}/blog/posts/${slug}`)
      .then(r => {
        if (r.status === 404) { setNotFound(true); return null; }
        return r.ok ? r.json() : null;
      })
      .then(d => { if (d) setPost(d); })
      .catch(() => setNotFound(true))
      .finally(() => setIsLoading(false));
  }, [slug]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#fafaf9] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#1e3a8a] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (notFound || !post) {
    return (
      <div className="min-h-screen bg-[#fafaf9] flex flex-col items-center justify-center gap-4">
        <BookOpen className="w-12 h-12 text-stone-300" />
        <h1 className="font-serif text-2xl font-bold text-stone-600">Post not found</h1>
        <Link to="/blog" className="text-[#1e3a8a] hover:underline text-sm">← Back to Blog</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fafaf9]">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-stone-200/50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button
              onClick={() => navigate('/blog')}
              className="flex items-center gap-2 text-stone-600 hover:text-[#1e3a8a] transition-colors text-sm"
            >
              <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
              <span className="hidden sm:inline">All Posts</span>
            </button>
            <Link to="/" className="font-serif text-xl font-bold text-[#1e3a8a]">SermonSift</Link>
            <Link to="/dashboard" className="text-sm text-[#1e3a8a] hover:underline hidden sm:block">
              Dashboard
            </Link>
          </div>
        </div>
      </header>

      {/* Featured image */}
      {post.featured_image && (
        <div className="w-full h-72 sm:h-96 overflow-hidden">
          <img
            src={post.featured_image}
            alt={post.title}
            className="w-full h-full object-cover"
          />
        </div>
      )}

      {/* Article */}
      <article className="max-w-3xl mx-auto px-6 py-12">

        {/* Meta */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          {(post.tags || []).map(t => (
            <Link
              key={t}
              to={`/blog?tag=${encodeURIComponent(t)}`}
              className="inline-flex items-center gap-1 bg-[#1e3a8a]/8 text-[#1e3a8a] text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full hover:bg-[#1e3a8a]/15 transition-colors"
            >
              <Tag className="w-3 h-3" /> {t}
            </Link>
          ))}
          <span className="flex items-center gap-1.5 text-stone-400 text-sm">
            <Calendar className="w-3.5 h-3.5" />
            {formatDate(post.published_at)}
          </span>
          {post.author && post.author !== 'SermonSift' && (
            <span className="text-stone-400 text-sm">by {post.author}</span>
          )}
        </div>

        {/* Title */}
        <h1 className="font-serif text-3xl sm:text-4xl font-bold text-[#1e3a8a] leading-tight mb-8">
          {post.title}
        </h1>

        {/* Content */}
        <div
          className="prose prose-stone prose-lg max-w-none
            prose-headings:font-serif prose-headings:text-[#1e3a8a]
            prose-a:text-[#0ea5e9] prose-a:no-underline hover:prose-a:underline
            prose-blockquote:border-l-[#C9A84C] prose-blockquote:bg-amber-50/50 prose-blockquote:py-1
            prose-strong:text-[#1f2937]
            prose-img:rounded-xl prose-img:shadow-md"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />

        {/* Footer */}
        <div className="mt-14 pt-8 border-t border-stone-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <p className="text-xs text-stone-400 uppercase tracking-wider font-semibold mb-1">Published by</p>
            <p className="font-serif text-[#1e3a8a] font-bold">SermonSift</p>
            <p className="text-stone-400 text-sm">AI-powered sermon analysis for ministry leaders</p>
          </div>
          <Link
            to="/signup"
            className="btn-primary text-sm px-5 py-2.5 rounded-xl whitespace-nowrap"
          >
            Try SermonSift Free
          </Link>
        </div>
      </article>

      {/* Back to blog */}
      <div className="max-w-3xl mx-auto px-6 pb-16">
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 text-stone-500 hover:text-[#1e3a8a] text-sm transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back to all posts
        </Link>
      </div>

      {/* Footer */}
      <footer className="py-10 border-t border-stone-200 bg-[#fafaf9]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <span className="font-serif text-lg font-bold text-[#1e3a8a]">SermonSift</span>
          <p className="text-sm text-stone-400">© {new Date().getFullYear()} Bethany Labs. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
